import { z } from "zod";
import type { ProductionWorkspacePlanRow, ProductionWorkspaceActualRow, ProductionWorkspaceReport } from "../../../shared/production/workspace-report";
import type { SavedProductionPlanSummary } from "../../../shared/production/production-plan-summary";
import type { SnapshotView } from "./public-daily-report-types";

const text = z.string(), nullableText = text.nullable(), strings = z.array(text);
const level = z.enum(["FINAL_19", "INTERMEDIATE_21", "INTERMEDIATE_22", "OTHER"]);
const plan = z.looseObject({
  id: text, weekStart: z.iso.date(), weekEnd: text, version: z.number(), status: z.enum(["ACTIVE", "SUPERSEDED"]),
  fileFingerprint: text, sheetName: text, createdAt: text, refreshedAt: text, origin: z.enum(["SYSTEM", "EXCEL"]).optional(),
  summary: z.looseObject({
    importedRowCount: z.number(), internalProductionRowCount: z.number(), ignoredInternalRowCount: z.number(),
    waitingWorkOrderCount: z.number(), missingWorkOrderCount: z.number(), ambiguousWorkOrderCount: z.number(),
    matchedWorkOrderCount: z.number(), partialRowCount: z.number(), completedRowCount: z.number(), checkRequiredCount: z.number(),
    materialCount: z.number().nullable(), shortageCount: z.number().nullable(),
  }),
}) satisfies z.ZodType<SavedProductionPlanSummary>;
const planRow = z.looseObject({
  id: text, date: text, productCode: text, productName: text, level, unit: nullableText, quantity: z.number(),
  lineName: nullableText, workOrderNo: nullableText, shiftCode: nullableText, plannedStartAt: nullableText, plannedEndAt: nullableText,
  sourceRow: z.number(), issues: strings,
}) satisfies z.ZodType<ProductionWorkspacePlanRow>;
const actualRow = z.looseObject({
  id: text, date: text, productCode: text, productName: text, level, unit: nullableText, unitWeightKg: z.number().nullable(), quantity: z.number(),
  lineName: nullableText, documentShiftCode: z.enum(["VR1", "VR2", "VR3"]).nullable(), workOrderNo: text,
  workOrderCreatedAt: nullableText, workOrderStatus: z.enum(["PLANNED", "ACTIVE", "CLOSED", "UNKNOWN"]),
  workOrderPlannedStartAt: nullableText, workOrderPlannedEndAt: nullableText, parentWorkOrderNo: nullableText,
  rootWorkOrderNo: nullableText, rootOutputCode: nullableText, rootOutputName: nullableText, rootDepth: z.number().nullable(),
  recordedAt: nullableText, documentNo: nullableText, batchCode: nullableText, lotNo: nullableText,
  linkStatus: z.enum(["FINAL", "VERIFIED_CHAIN", "UNRESOLVED"]), isScrap: z.boolean(), productInWeeklyPlan: z.boolean().nullable(), issues: strings,
}) satisfies z.ZodType<ProductionWorkspaceActualRow>;
const report = z.looseObject({
  source: z.literal("PRODUCTION_PLAN_AND_MIKRO"), plan: plan.nullable(), range: z.looseObject({from: text, to: text}),
  observedAt: text, generatedAt: text, today: text,
  // Eski snapshot'larda kaynak yoksa hedefi doğrulanmış kabul etme.
  dailyPlanBasis: z.enum(["EXCEL_DAILY", "SYSTEM_DAILY", "UNVERIFIED"]).default("UNVERIFIED"),
  planRows: z.array(planRow), actualRows: z.array(actualRow), truncated: z.boolean(), limitations: strings,
}) satisfies z.ZodType<ProductionWorkspaceReport>;
const snapshotResponse = z.looseObject({
  snapshot: z.looseObject({id: text, productionDate: z.iso.date(), generatedAt: text, sourceObservedAt: text, expiresAt: nullableText}),
  report,
}) satisfies z.ZodType<SnapshotView>;

export function parsePublicDailyReport(value: unknown): SnapshotView {
  const parsed = snapshotResponse.safeParse(value);
  if (!parsed.success) throw new Error("Rapor verisi eksik veya geçersiz. Raporu Üretim Raporları ekranından yeniden oluşturun.");
  return parsed.data;
}
export function publicDailyReportError(value: unknown): string {
  const parsed = z.object({message: z.string().trim().min(1)}).safeParse(value);
  return parsed.success ? parsed.data.message : "Rapor bağlantısı geçersiz veya süresi dolmuş.";
}
